import { Pause, Play, RotateCcw } from "lucide-react";
import { formatPlayhead, type VideoPlayhead } from "./useVideoPlayhead";

/** Clip-local transport shared by coach preview and player feedback. */
export function ViewerClipControls({
  video,
  start,
  end,
  playhead,
  exactWindow = false,
}: {
  video: HTMLVideoElement | null;
  start: number;
  end: number;
  playhead: VideoPlayhead;
  /** Canonical pins permit durations shorter than the legacy UI minimum. */
  exactWindow?: boolean;
}) {
  const safeStart = Math.max(0, Number(start) || 0);
  const validWindow = !exactWindow || (Number.isFinite(start) && Number.isFinite(end) && end > safeStart);
  const safeEnd = exactWindow ? (validWindow ? end : safeStart) : Math.max(safeStart + 0.1, Number(end) || safeStart + 1);
  const current = Math.min(
    safeEnd,
    Math.max(safeStart, playhead.currentTime ?? safeStart),
  );
  const clipDuration = safeEnd - safeStart;
  const elapsed = current - safeStart;

  const restart = (play: boolean) => {
    if (!video || !validWindow) return;
    video.currentTime = safeStart;
    if (play) void video.play().catch(() => undefined);
  };

  const toggle = () => {
    if (!video || !validWindow) return;
    if (video.paused) {
      if (video.currentTime < safeStart || video.currentTime >= safeEnd) {
        video.currentTime = safeStart;
      }
      void video.play().catch(() => undefined);
    } else {
      video.pause();
    }
  };

  return (
    <div className="feedback-clip-playback" data-testid="feedback-clip-playback">
      <div className="feedback-clip-playback-row">
        <button type="button" onClick={toggle} disabled={!video || !validWindow}>
          {playhead.paused ? <Play aria-hidden /> : <Pause aria-hidden />}
          {playhead.paused ? "Play preview" : "Pause preview"}
        </button>
        <input
          className="tktic-range"
          type="range"
          min={0}
          max={clipDuration}
          step={exactWindow ? "any" : "0.05"}
          value={elapsed}
          aria-label="Clip playhead"
          aria-valuetext={`${formatPlayhead(elapsed)} of ${formatPlayhead(clipDuration)}`}
          disabled={!video || !validWindow}
          onChange={(event) => {
            if (video && validWindow) {
              video.currentTime =
                Math.min(safeEnd, Math.max(safeStart, safeStart + Number(event.currentTarget.value)));
            }
          }}
        />
        <span>
          {formatPlayhead(elapsed)} / {formatPlayhead(clipDuration)}
        </span>
        <button
          type="button"
          className="feedback-clip-restart"
          onClick={() => restart(false)}
          disabled={!video || !validWindow}
        >
          <RotateCcw aria-hidden /> Restart
        </button>
      </div>
      <p>Saved clip · match recording</p>
    </div>
  );
}
